import { useId, useState } from "react";
import type { QueueEmployee } from "@/lib/staff-api";
import { formatDollars, formatPhone } from "@/lib/formatters";
import { useEmployeeAction } from "@/lib/staff-hooks";
import { latestClearanceDate } from "@/lib/staff-stages";
import type { EmployeeAction } from "@/lib/staff-hooks";
import { ActionError } from "./ActionError";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "./buttons";
import { StaffDialog } from "./StaffDialog";


interface ClearanceFormProps {
  initialPhoto: boolean;
  pending: boolean;
  onFound: (clearanceDate: string, photoOnFile: boolean) => void;
  children?: React.ReactNode;
}

/** The date field, the photo question and "Found in Clearinghouse". The date
 *  is the one the Clearinghouse shows, never today's: it anchors the badge's
 *  expiry. `children` sits beside the Found button (Verify's "Not found"). */
function ClearanceForm({ initialPhoto, pending, onFound, children }: ClearanceFormProps): React.ReactElement {
  const ids = useId();
  const [clearanceDate, setClearanceDate] = useState("");
  const [photoOnFile, setPhotoOnFile] = useState(initialPhoto);
  const [missingDate, setMissingDate] = useState(false);

  const found = (): void => {
    setMissingDate(!clearanceDate);
    if (clearanceDate) onFound(clearanceDate, photoOnFile);
  };

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor={`${ids}-date`} className="block font-medium text-gray-900">
            Clearance date shown in the Clearinghouse
          </label>
          <input
            id={`${ids}-date`}
            type="date"
            max={latestClearanceDate()}
            value={clearanceDate}
            onChange={(e) => setClearanceDate(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-2"
          />
        </div>
        <label className="flex items-center gap-2 pb-2 font-medium text-gray-900">
          <input type="checkbox" checked={photoOnFile} onChange={(e) => setPhotoOnFile(e.target.checked)} />
          Photo on file?
        </label>
      </div>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={found} disabled={pending} className={PRIMARY_BUTTON}>
          Found in Clearinghouse
        </button>
        {children}
      </div>
      {missingDate && (
        <p role="alert" className="text-red-700">
          Enter the clearance date shown in the Clearinghouse.
        </p>
      )}
    </div>
  );
}

/** Confirms clearance on the Clearinghouse date, with the photo answer. */
function useClearance(employee: QueueEmployee): EmployeeAction & {
  confirm: (clearanceDate: string, photoOnFile: boolean) => void;
} {
  const action = useEmployeeAction(employee);
  return {
    ...action,
    confirm: (clearanceDate, photoOnFile) => action.run({ action: "confirm_clearance", clearanceDate, photoOnFile }),
  };
}

/** A fingerprint result arrived: record the clearance. */
export function ClearanceResult({ employee }: { employee: QueueEmployee }): React.ReactElement {
  const { mutation, confirm, reload } = useClearance(employee);
  return (
    <div>
      <ClearanceForm initialPhoto={employee.photoOnFile === true} pending={mutation.isPending} onFound={confirm} />
      <ActionError error={mutation.error} onReload={reload} />
    </div>
  );
}

function VendorContactLine({ employee }: { employee: QueueEmployee }): React.ReactElement {
  const vendor = employee.vendor ?? null;
  if (!vendor) return <p className="text-sm text-gray-600">Vendor contact not on file</p>;
  return (
    <p className="flex flex-wrap gap-x-3 text-sm text-gray-700">
      <span>{vendor.contactName}</span>
      <span>{formatPhone(vendor.contactPhone)}</span>
      <a href={`mailto:${vendor.email}`} className="text-blue-700 underline">
        {vendor.email}
      </a>
    </p>
  );
}

/**
 * Verify one attested name: two buttons and a date field. "Not found" needs no
 * input but emails the vendor, so it sits behind an in-app confirmation.
 */
export function VerifyPanel({ employee }: { employee: QueueEmployee }): React.ReactElement {
  const { mutation, confirm, reload, run } = useClearance(employee);
  const [askingNotFound, setAskingNotFound] = useState(false);
  const name = `${employee.firstName} ${employee.lastName}`;
  const company = employee.vendor?.companyName ?? "the vendor";
  const owed =
    typeof employee.completionPriceOwed === "number"
      ? `the remaining ${formatDollars(employee.completionPriceOwed)}`
      : "the remaining balance";

  const markNotFound = (): void => {
    setAskingNotFound(false);
    run({ action: "not_found" });
  };

  return (
    <div className="space-y-3">
      <VendorContactLine employee={employee} />
      <ClearanceForm initialPhoto={employee.photoOnFile === true} pending={mutation.isPending} onFound={confirm}>
        <button
          type="button"
          onClick={() => setAskingNotFound(true)}
          disabled={mutation.isPending}
          className={SECONDARY_BUTTON}
        >
          Not found
        </button>
      </ClearanceForm>
      <ActionError error={mutation.error} onReload={reload} />
      {askingNotFound && (
        <StaffDialog title="Not found in the Clearinghouse?" onClose={() => setAskingNotFound(false)}>
          <p className="text-sm text-gray-700">
            This emails {company} that {name} was not found and owes {owed}. Continue?
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => setAskingNotFound(false)} className={SECONDARY_BUTTON}>
              Cancel
            </button>
            <button type="button" onClick={markNotFound} className={PRIMARY_BUTTON}>
              Mark not found and email vendor
            </button>
          </div>
        </StaffDialog>
      )}
    </div>
  );
}
